import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAuth from "@/hooks/useAuth";
import companiesApi from "@/api/companiesApi";
import ApiError from "@/model/ApiError";
import CompanyResponse from "@/model/CompanyResponse";
import CompanyStatus from "@/model/CompanyStatus";
import "@/pages/EditCompanyPage/EditCompanyPage.scss";

const STATUS_OPTIONS: CompanyStatus[] = [
  "PENDING_VERIFICATION",
  "ACTIVE",
  "SUSPENDED",
];

function trimOrUndefined(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function EditCompanyPage() {
  const { t } = useTranslation();
  const { session } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<"notFound" | "generic" | null>(
    null
  );

  const [name, setName] = useState("");
  const [registrationNumber, setRegistrationNumber] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("");
  const [website, setWebsite] = useState("");
  const [description, setDescription] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [status, setStatus] = useState<CompanyStatus>("PENDING_VERIFICATION");

  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!session || !id) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setLoadError(null);

    companiesApi
      .getCompanyById(session.accessToken, id)
      .then((company: CompanyResponse) => {
        if (cancelled) {
          return;
        }
        setName(company.name ?? "");
        setRegistrationNumber(company.registrationNumber ?? "");
        setEmail(company.email ?? "");
        setPhoneNumber(company.phoneNumber ?? "");
        setStreet(company.address?.street ?? "");
        setCity(company.address?.city ?? "");
        setPostalCode(company.address?.postalCode ?? "");
        setCountry(company.address?.country ?? "");
        setWebsite(company.website ?? "");
        setDescription(company.description ?? "");
        setLogoUrl(company.logoUrl ?? "");
        setStatus(company.status ?? "PENDING_VERIFICATION");
      })
      .catch((err) => {
        if (cancelled) {
          return;
        }
        if (err instanceof ApiError && err.status === 404) {
          setLoadError("notFound");
        } else {
          setLoadError("generic");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [session, id]);

  if (!session || !id) {
    return null;
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const address = {
        street: trimOrUndefined(street),
        city: trimOrUndefined(city),
        postalCode: trimOrUndefined(postalCode),
        country: trimOrUndefined(country),
      };
      const hasAddress = Object.values(address).some(
        (value) => value !== undefined
      );

      await companiesApi.updateCompany(session.accessToken, id, {
        name: trimOrUndefined(name),
        registrationNumber: trimOrUndefined(registrationNumber),
        email: trimOrUndefined(email),
        phoneNumber: trimOrUndefined(phoneNumber),
        address: hasAddress ? address : undefined,
        website: trimOrUndefined(website),
        description: trimOrUndefined(description),
        logoUrl: trimOrUndefined(logoUrl),
        status,
      });
      navigate(`/companies/${id}`, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setError(t("companies.edit.validationError"));
      } else {
        setError(t("companies.edit.genericError"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="edit-company-page">
      {isLoading && <p>{t("companies.edit.loading")}</p>}
      {!isLoading && loadError === "notFound" && (
        <p role="alert">{t("companies.edit.notFound")}</p>
      )}
      {!isLoading && loadError === "generic" && (
        <p role="alert">{t("companies.edit.loadError")}</p>
      )}

      {!isLoading && !loadError && (
        <form className="edit-company-page__form" onSubmit={handleSubmit}>
          <h1>{t("companies.edit.title")}</h1>

          <label htmlFor="name">{t("companies.edit.nameLabel")}</label>
          <input
            id="name"
            type="text"
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
          />

          <label htmlFor="status">{t("companies.edit.statusLabel")}</label>
          <select
            id="status"
            value={status}
            onChange={(event) => setStatus(event.target.value as CompanyStatus)}
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {t(`companies.status.${option}`)}
              </option>
            ))}
          </select>

          <label htmlFor="registrationNumber">
            {t("companies.edit.registrationNumberLabel")}
          </label>
          <input
            id="registrationNumber"
            type="text"
            value={registrationNumber}
            onChange={(event) => setRegistrationNumber(event.target.value)}
          />

          <label htmlFor="email">{t("companies.edit.emailLabel")}</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />

          <label htmlFor="phoneNumber">
            {t("companies.edit.phoneNumberLabel")}
          </label>
          <input
            id="phoneNumber"
            type="tel"
            value={phoneNumber}
            onChange={(event) => setPhoneNumber(event.target.value)}
          />

          <label htmlFor="street">{t("companies.edit.streetLabel")}</label>
          <input
            id="street"
            type="text"
            value={street}
            onChange={(event) => setStreet(event.target.value)}
          />

          <label htmlFor="city">{t("companies.edit.cityLabel")}</label>
          <input
            id="city"
            type="text"
            value={city}
            onChange={(event) => setCity(event.target.value)}
          />

          <label htmlFor="postalCode">
            {t("companies.edit.postalCodeLabel")}
          </label>
          <input
            id="postalCode"
            type="text"
            value={postalCode}
            onChange={(event) => setPostalCode(event.target.value)}
          />

          <label htmlFor="country">{t("companies.edit.countryLabel")}</label>
          <input
            id="country"
            type="text"
            value={country}
            onChange={(event) => setCountry(event.target.value)}
          />

          <label htmlFor="website">{t("companies.edit.websiteLabel")}</label>
          <input
            id="website"
            type="text"
            value={website}
            onChange={(event) => setWebsite(event.target.value)}
          />

          <label htmlFor="logoUrl">{t("companies.edit.logoUrlLabel")}</label>
          <input
            id="logoUrl"
            type="text"
            value={logoUrl}
            onChange={(event) => setLogoUrl(event.target.value)}
          />

          <label htmlFor="description">
            {t("companies.edit.descriptionLabel")}
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            maxLength={2000}
          />

          {error && <p className="edit-company-page__error">{error}</p>}

          <div className="edit-company-page__actions">
            <button type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? t("companies.edit.submitting")
                : t("companies.edit.submit")}
            </button>
            <Link to={`/companies/${id}`}>{t("companies.edit.cancel")}</Link>
          </div>
        </form>
      )}
    </div>
  );
}

export default EditCompanyPage;
